import {
  PartImageLabelChecks,
  PartImageLabelEvidence,
  PartImageLabelFieldReading,
  PartImageLabelFieldReadings,
} from '@/api/generatedApi';
import { ConfidenceBand, LABEL_READING_CONFIDENCE } from '@/components/PartFinder/confidence/confidence';
import { ConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import { FadeIn } from '@/components/PartFinder/FadeIn';
import { useGetPartImageLabelCrop } from '@/hooks/api/part-finder-image-search/useGetPartImageLabelCrop';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const CROP_HEIGHT = 200;
const STAGGER_MS = 80;

export interface PendingLabelCard {
  key: string;
  caption?: string;
}

const LabelCard = styled(FadeIn)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));

const FieldGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  columnGap: '12px',
  rowGap: '2px',
  alignItems: 'center',
});

const FIELDS: [keyof PartImageLabelFieldReadings, string][] = [
  ['nsn', 'NSN'],
  ['partNumber', 'Part number'],
  ['cage', 'CAGE'],
  ['manufacturer', 'Manufacturer'],
  ['serial', 'Serial'],
  ['contract', 'Contract'],
  ['lotDate', 'Lot/date'],
];

const CONFIDENCE_LABELS: Record<PartImageLabelFieldReading['confidence'], string> = {
  high: 'High',
  medium: 'Med',
  low: 'Low',
};

const LABEL_READING_RANGES: Record<ConfidenceBand, string> = { high: 'High', medium: 'Med', low: 'Low' };

const CHECKS: [keyof PartImageLabelChecks, passed: string, failed: string][] = [
  ['nsnFormat', 'NSN format valid', 'NSN format invalid'],
  ['nsnInFlis', 'NSN in FLIS', 'NSN not in FLIS'],
  ['cageInFlis', 'CAGE in FLIS', 'CAGE not in FLIS'],
  ['partNumberWithCageInFlis', 'Part number listed under this CAGE', 'Part number not listed under this CAGE'],
];

const staggered = (index: number) => ({ animationDelay: `${index * STAGGER_MS}ms` });

const FieldCaption = ({ children }: { children: string }) => (
  <Typography variant='caption' color='text.secondary'>
    {children}
  </Typography>
);

const LabelCrop = ({ identificationId, label }: { identificationId: string; label: PartImageLabelEvidence }) => {
  const { image, error } = useGetPartImageLabelCrop(identificationId, label.index);
  if (error) {
    return (
      <Typography variant='caption' color='text.secondary'>
        Crop unavailable
      </Typography>
    );
  }
  if (!image) return <Skeleton variant='rounded' height={CROP_HEIGHT} />;
  return (
    <FadeIn>
      <Box
        component='img'
        src={image}
        alt={`Cropped ${label.kind} from photo ${label.photoNumber}`}
        sx={{ display: 'block', width: '100%', maxHeight: CROP_HEIGHT, objectFit: 'contain', borderRadius: '4px' }}
        data-testid='part-image-label-crop'
      />
    </FadeIn>
  );
};

const LabelFields = ({ fields }: { fields: PartImageLabelFieldReadings }) => (
  <FieldGrid>
    {FIELDS.map(([name, caption]) => {
      const field = fields[name];
      return (
        <Box key={name} sx={{ display: 'contents' }} data-testid={`part-image-label-field-${name}`}>
          <FieldCaption>{caption}</FieldCaption>
          <Typography
            variant='body2'
            sx={{ overflowWrap: 'anywhere' }}
            color={field ? 'text.primary' : 'text.disabled'}
          >
            {field?.value ?? 'Not visible'}
          </Typography>
          <Box>
            {field && (
              <ConfidencePill
                band={field.confidence}
                label={CONFIDENCE_LABELS[field.confidence]}
                explanation={LABEL_READING_CONFIDENCE}
                ranges={LABEL_READING_RANGES}
              />
            )}
          </Box>
        </Box>
      );
    })}
  </FieldGrid>
);

interface CheckResult {
  name: keyof PartImageLabelChecks;
  passed: boolean;
  text: string;
}

const checkResults = (checks: PartImageLabelChecks): CheckResult[] =>
  CHECKS.flatMap(([name, passed, failed]) => {
    const result = checks[name];
    return result === undefined ? [] : [{ name, passed: result, text: result ? passed : failed }];
  });

const LabelChecks = ({ results }: { results: CheckResult[] }) => (
  <Stack direction='row' columnGap='12px' rowGap='4px' flexWrap='wrap'>
    {results.map(({ name, passed, text }) => (
      <Stack key={name} direction='row' gap='4px' alignItems='center' data-testid={`part-image-label-check-${name}`}>
        {passed ? (
          <CheckCircle fontSize='inherit' color='success' aria-label='passed' />
        ) : (
          <Cancel fontSize='inherit' color='error' aria-label='failed' />
        )}
        <Typography variant='caption'>{text}</Typography>
      </Stack>
    ))}
  </Stack>
);

const ReadLabel = ({
  identificationId,
  label,
  index,
}: {
  identificationId: string;
  label: PartImageLabelEvidence;
  index: number;
}) => {
  const results = checkResults(label.checks);
  return (
    <LabelCard style={staggered(index)} data-testid='part-image-label'>
      <LabelCrop identificationId={identificationId} label={label} />
      <FieldCaption>{`${label.kind} · photo ${label.photoNumber}`}</FieldCaption>
      <LabelFields fields={label.fields} />
      {results.length > 0 && <LabelChecks results={results} />}
    </LabelCard>
  );
};

const PendingLabel = ({ card, index }: { card: PendingLabelCard; index: number }) => (
  <LabelCard style={staggered(index)} data-testid='part-image-label-pending'>
    <Skeleton variant='rounded' height={CROP_HEIGHT} />
    {card.caption ? (
      <FieldCaption>{`${card.caption} · reading it at full resolution`}</FieldCaption>
    ) : (
      <Skeleton variant='text' width='45%' />
    )}
    <FieldGrid>
      {FIELDS.map(([name, caption]) => (
        <Box key={name} sx={{ display: 'contents' }}>
          <FieldCaption>{caption}</FieldCaption>
          <Skeleton variant='text' width='70%' />
          <span />
        </Box>
      ))}
    </FieldGrid>
  </LabelCard>
);

export const LabelEvidence = ({
  identificationId,
  labels,
  pending = [],
}: {
  identificationId: string;
  labels: PartImageLabelEvidence[];
  pending?: PendingLabelCard[];
}) => (
  <Stack gap='8px' data-testid='part-image-label-evidence'>
    <Typography variant='subtitle2'>Label evidence</Typography>
    {labels.length === 0 && pending.length === 0 && (
      <Typography variant='body2' color='text.secondary' data-testid='part-image-no-labels'>
        No label, nameplate, or stamping was found in the photos.
      </Typography>
    )}
    {labels.map((label, index) => (
      <ReadLabel key={label.index} identificationId={identificationId} label={label} index={index} />
    ))}
    {pending.map((card, index) => (
      <PendingLabel key={card.key} card={card} index={labels.length + index} />
    ))}
  </Stack>
);
